import {CheckCircleOutlined, HistoryOutlined,} from '@ant-design/icons';
import {Button, Col, Row, Space, Tag, Typography} from 'antd';
import React, {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import PageContainer from '../../components/common/PageContainer';
import {useVariablesGroup} from '../../hooks/parameters';
import {useScaleBands} from '../../hooks/useScaleBands';
import {useTranslation} from '../../hooks/useTranslation';
import AssessmentOption from './assessment/AssessmentOption';
import AssessmentOptionDrawer from './assessment/AssessmentOptionDrawer';
import AssessmentVariableDrawer from './assessment/AssessmentVariableDrawer';
import AssessmentVariableTree from './assessment/AssessmentVariableTree';
import ScaleBandDrawer from './assessment/ScaleBandDrawer';
import ScaleBandPreview from './assessment/ScaleBandPreview';
import ScaleBandTable from './assessment/ScaleBandTable';
import {accentForId, findParentGroup, getDisplayText, monoFromName} from './assessment/helpers';
import './styles/index.less';

const {Text, Title} = Typography;

const formatTpl = (tpl: string, vars: Record<string, string | number>): string =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));

const AssessmentConfig: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const {data: scaleBands = []} = useScaleBands();
  const {data: variablesGroup = []} = useVariablesGroup();

  const [currentStep, setCurrentStep] = useState(0);

  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null);
  const [selectedVariableId, setSelectedVariableId] = useState<string | number | null>(null);
  const [selectedVariableGroupId, setSelectedVariableGroupId] = useState<string | number | null>(null);
  const [selectedNodeName, setSelectedNodeName] = useState<string>('');
  const [isParentNode, setIsParentNode] = useState(false);

  const [bandDrawerOpen, setBandDrawerOpen] = useState(false);
  const [editingBand, setEditingBand] = useState<any | null>(null);

  const [valueDrawerOpen, setValueDrawerOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<any | null>(null);

  const [nodeDrawerOpen, setNodeDrawerOpen] = useState(false);
  const [nodeDrawerType, setNodeDrawerType] = useState<'group' | 'variable'>('group');
  const [editingNode, setEditingNode] = useState<any | null>(null);
  const [parentGroupId, setParentGroupId] = useState<string | number | null>(null);

  const handleTreeSelect = (item: any, key: string) => {
    setSelectedNodeKey(key);
    setSelectedNodeName(getDisplayText(item));
    if (item?.idVariable != null) {
      setIsParentNode(false);
      setSelectedVariableId(item.idVariable);
      setSelectedVariableGroupId(null);
    } else if (item?.idVariableGroup != null) {
      setIsParentNode(true);
      setSelectedVariableGroupId(item.idVariableGroup);
      setSelectedVariableId(null);
    } else {
      setIsParentNode(false);
      setSelectedVariableId(item?.id ?? null);
      setSelectedVariableGroupId(null);
    }
  };

  const openCreateBand = () => {
    setEditingBand(null);
    setBandDrawerOpen(true);
  };
  const openEditBand = (record: any) => {
    setEditingBand(record);
    setBandDrawerOpen(true);
  };
  const closeBandDrawer = () => {
    setBandDrawerOpen(false);
    setEditingBand(null);
  };

  const openCreateValue = () => {
    setEditingValue(null);
    setValueDrawerOpen(true);
  };
  const openEditValue = (record: any) => {
    setEditingValue(record);
    setValueDrawerOpen(true);
  };
  const closeValueDrawer = () => {
    setValueDrawerOpen(false);
    setEditingValue(null);
  };

  const openCreateGroup = () => {
    setNodeDrawerType('group');
    setEditingNode(null);
    setParentGroupId(null);
    setNodeDrawerOpen(true);
  };
  const openEditGroup = (item: any) => {
    setNodeDrawerType('group');
    setEditingNode(item);
    setParentGroupId(null);
    setNodeDrawerOpen(true);
  };
  const openCreateVariable = (parent: any) => {
    setNodeDrawerType('variable');
    setEditingNode(null);
    setParentGroupId(parent.idVariableGroup);
    setNodeDrawerOpen(true);
  };
  const openEditVariable = (item: any) => {
    setNodeDrawerType('variable');
    setEditingNode(item);
    setParentGroupId(null);
    setNodeDrawerOpen(true);
  };
  const closeNodeDrawer = () => {
    setNodeDrawerOpen(false);
    setEditingNode(null);
    setParentGroupId(null);
  };

  const selectedGroup = useMemo(() => {
    if (isParentNode && selectedVariableGroupId != null) {
      return variablesGroup.find((g: any) => String(g.idVariableGroup) === String(selectedVariableGroupId));
    }
    return findParentGroup(variablesGroup, selectedVariableId);
  }, [variablesGroup, isParentNode, selectedVariableId, selectedVariableGroupId]);

  const selectedGroupName = selectedGroup ? getDisplayText(selectedGroup) : undefined;
  const selectedGroupAccent = selectedGroup
    ? accentForId(selectedGroup.idVariableGroup)
    : undefined;
  const selectedGroupMono = selectedGroup ? monoFromName(getDisplayText(selectedGroup)) : undefined;

  const totalSteps = 2;
  const pillText = (i: number) =>
    formatTpl(t('settings.content.impactAssessment.steps.pillFormat'), {
      current: i + 1,
      total: totalSteps,
    });

  // ---- Render ----

  const headerExtra = (
    <Space size="small" wrap>
      <Button icon={<HistoryOutlined/>} disabled>
        {t('settings.content.impactAssessment.header.actions.history')}
      </Button>
    </Space>
  );

  return (
    <PageContainer
      title={t('settings.content.impactAssessment.title')}
      subTitle={t('settings.content.impactAssessment.description')}
      titleClassName="h3 gt-text-color-neutral-800"
      extra={headerExtra}
      headerPaddingBottom={true}
    >
      <Row gutter={[8, 8]} className="gt-ac-wizard">
        <Col xs={24} md={6} lg={6} xl={6} xxl={4}>
          <div className="gt-ac-steps">
            <StepCard
              active={currentStep === 0}
              done={currentStep > 0}
              index={1}
              title={t('settings.content.impactAssessment.steps.scale.title')}
              subtitle={t('settings.content.impactAssessment.steps.scale.subtitle')}
              onClick={() => setCurrentStep(0)}
            />
            <StepCard
              active={currentStep === 1}
              done={false}
              index={2}
              title={t('settings.content.impactAssessment.steps.variables.title')}
              subtitle={t('settings.content.impactAssessment.steps.variables.subtitle')}
              onClick={() => setCurrentStep(1)}
            />
          </div>
        </Col>

        <Col xs={24} md={18} lg={18} xl={18} xxl={20}>
          <div className="gt-ac-panel">

            {currentStep === 0 && (
              <>
                <ScaleBandPreview bands={scaleBands}/>
                <ScaleBandTable
                  onCreate={openCreateBand}
                  onEdit={openEditBand}
                  drawerOpen={bandDrawerOpen}
                />
              </>
            )}

            {currentStep === 1 && (
              <Row gutter={[24, 24]}>
                <Col xs={24} md={24} lg={10} xl={10} xxl={10}>
                  <AssessmentVariableTree
                    selectedKey={selectedNodeKey}
                    onSelect={handleTreeSelect}
                    onCreateGroup={openCreateGroup}
                    onEditGroup={openEditGroup}
                    onCreateVariable={openCreateVariable}
                    onEditVariable={openEditVariable}
                  />
                </Col>
                <Col xs={24} md={24} lg={14} xl={14} xxl={14}>
                  <AssessmentOption
                    selectedNodeKey={selectedNodeKey}
                    selectedNodeName={selectedNodeName}
                    selectedVariableId={selectedVariableId}
                    selectedVariableGroupId={selectedVariableGroupId}
                    isParentNode={isParentNode}
                    groupName={selectedGroupName}
                    groupAccent={selectedGroupAccent}
                    groupMono={selectedGroupMono}
                    onCreate={openCreateValue}
                    onEdit={openEditValue}
                    drawerOpen={valueDrawerOpen}
                  />
                </Col>
              </Row>
            )}

            <div className="gt-ac-footer">
              <div/>
              <Space>
                <Button
                  disabled={currentStep === 0}
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  {t('settings.content.impactAssessment.buttons.previous')}
                </Button>
                {currentStep === totalSteps - 1 ? (
                  <Button type="primary" onClick={() => navigate('/settings')}>
                    {t('settings.content.impactAssessment.buttons.finish')}
                  </Button>
                ) : (
                  <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
                    {t('settings.content.impactAssessment.buttons.next')}
                  </Button>
                )}
              </Space>
            </div>
          </div>
        </Col>
      </Row>

      <ScaleBandDrawer
        open={bandDrawerOpen}
        record={editingBand}
        onClose={closeBandDrawer}
      />

      <AssessmentOptionDrawer
        open={valueDrawerOpen}
        record={editingValue}
        isParentNode={isParentNode}
        selectedVariableId={selectedVariableId}
        selectedVariableGroupId={selectedVariableGroupId}
        targetName={selectedNodeName}
        onClose={closeValueDrawer}
      />

      <AssessmentVariableDrawer
        open={nodeDrawerOpen}
        type={nodeDrawerType}
        record={editingNode}
        parentGroupId={parentGroupId}
        onClose={closeNodeDrawer}
      />
    </PageContainer>
  );
};


interface StepCardProps {
  active: boolean;
  done: boolean;
  index: number;
  title: string;
  subtitle?: string;
  onClick: () => void;
}

const StepCard: React.FC<StepCardProps> = ({active, done, index, title, subtitle, onClick}) => (
  <button
    type="button"
    className={`gt-ac-step${active ? ' gt-ac-step-active' : ''}${done ? ' gt-ac-step-done' : ''}`}
    onClick={onClick}
  >
    <span className="gt-ac-step-num">{done ? <CheckCircleOutlined/> : index}</span>
    <span className="gt-ac-step-body">
      <span className="gt-ac-step-title">{title}</span>
      <span className="gt-ac-step-sub">{subtitle}</span>
    </span>
  </button>
);

export default AssessmentConfig;
